import EventEmitter from 'lib/helpers/EventEmmiter';
import { attachIframePlayer } from './media-iframe';

/**
 * MediaCoordinator — one place that decides what is audible in the viewer.
 *
 * Rule: when a foreground source starts audible playback, the previously
 * active foreground source is paused (it keeps its position, nothing is
 * destroyed). Background sources (looping ambient audio) pause while any
 * foreground source is active and resume once everything is idle.
 *
 * Sources:
 *  - native <audio>/<video> anywhere in the document are picked up
 *    automatically by a capture-phase 'play' listener (info-label audio,
 *    sidebar audio, ...). Mark an element `data-media="exempt"` to opt out.
 *  - detached elements (not in the DOM) and owners that want a named source
 *    call register() + bindElement().
 *  - YouTube / Vimeo iframes go through attachIframe().
 */

const RESUME_BACKGROUND_DELAY = 400;
const EXEMPT = 'exempt';

export default class MediaCoordinator extends EventEmitter {
    _sources = new Map();
    _active = null;
    _seq = 0;
    _resumeTimer = null;
    _elementIds = new WeakMap();

    constructor() {
        super();
        this._onCapturePlay = this._onCapturePlay.bind(this);
        document.addEventListener('play', this._onCapturePlay, true);
    }

    /**
     * @param {string} id
     * @param {{pause: Function, resume?: Function, layer?: 'foreground'|'background'}} options
     */
    register(id, { pause, resume, layer = 'foreground' } = {}) {
        this._sources.set(id, { id, pause, resume, layer, playing: false, suspended: false });

        return {
            id,
            started: () => this.started(id),
            stopped: () => this.stopped(id),
            unregister: () => this.unregister(id),
        };
    }

    unregister(id) {
        this.stopped(id);
        this._sources.delete(id);
    }

    started(id) {
        const source = this._sources.get(id);
        if (!source) return;
        source.playing = true;

        if (source.layer === 'background') {
            source.suspended = false;
            if (this._active) this._suspend(source);
            return;
        }

        clearTimeout(this._resumeTimer);
        if (this._active && this._active !== id) {
            const previous = this._sources.get(this._active);
            if (previous) {
                previous.playing = false;
                this._safe(previous.pause);
            }
        }
        this._active = id;
        this._sources.forEach(s => {
            if (s.layer === 'background' && s.playing) this._suspend(s);
        });
        this._fireEvent('change', id);
    }

    stopped(id) {
        const source = this._sources.get(id);
        if (!source) return;
        source.playing = false;
        if (source.layer === 'background' || this._active !== id) return;

        this._active = null;
        this._fireEvent('change', null);
        clearTimeout(this._resumeTimer);
        this._resumeTimer = setTimeout(() => this._resumeBackground(), RESUME_BACKGROUND_DELAY);
    }

    /** Pause the active foreground source (e.g. when e3play takes over). */
    pauseAll() {
        if (!this._active) return;
        const source = this._sources.get(this._active);
        this._active = null;
        if (source) {
            source.playing = false;
            this._safe(source.pause);
        }
    }

    /**
     * Report a media element's playback to the source `id`. Listeners sit on
     * the element itself, so they keep working for detached elements and for
     * elements removed from the DOM while playing.
     */
    bindElement(el, id) {
        if (!el || this._elementIds.get(el) === id) return;
        this._elementIds.set(el, id);
        el.dataset.mediaId = id;

        const update = () => {
            if (this._elementIds.get(el) !== id) return;
            if (!el.paused && !el.ended && this._isAudible(el)) this.started(id);
            else if (this._active === id || this._sources.get(id)?.layer === 'background') this.stopped(id);
        };
        ['play', 'playing', 'pause', 'ended', 'volumechange', 'emptied'].forEach(ev => el.addEventListener(ev, update));
    }

    /**
     * Track a YouTube/Vimeo iframe. Returns a detach() to call before the
     * iframe is removed (removed iframes send no further events).
     */
    attachIframe(iframe, { layer = 'foreground' } = {}) {
        const id = `iframe:${++this._seq}`;
        let player = null;
        const handle = this.register(id, { pause: () => player && player.pause(), layer });
        player = attachIframePlayer(iframe, {
            onPlaying: handle.started,
            onStopped: handle.stopped,
        });
        if (!player) {
            handle.unregister();
            return () => {};
        }

        return () => {
            player.destroy();
            handle.unregister();
        };
    }

    attachIframes(root) {
        const detachers = [...root.querySelectorAll('iframe')]
            .filter(iframe => iframe.dataset.media !== EXEMPT)
            .map(iframe => this.attachIframe(iframe));

        return () => detachers.forEach(detach => detach());
    }

    destroy() {
        clearTimeout(this._resumeTimer);
        document.removeEventListener('play', this._onCapturePlay, true);
        this._sources.clear();
        this._active = null;
        super.destroy();
    }

    _onCapturePlay(event) {
        const el = event.target;
        if (!(el instanceof HTMLMediaElement)) return;
        if (el.dataset.media === EXEMPT || this._elementIds.has(el)) return;

        const id = `element:${++this._seq}`;
        this.register(id, { pause: () => el.pause() });
        this.bindElement(el, id);
        if (this._isAudible(el)) this.started(id);
    }

    _isAudible(el) {
        return !el.muted && el.volume > 0;
    }

    _suspend(source) {
        source.playing = false;
        source.suspended = true;
        this._safe(source.pause);
    }

    _resumeBackground() {
        if (this._active) return;
        this._sources.forEach(s => {
            if (s.layer !== 'background' || !s.suspended) return;
            s.suspended = false;
            this._safe(s.resume);
        });
    }

    _safe(fn) {
        if (typeof fn !== 'function') return;
        try {
            const res = fn();
            if (res && typeof res.catch === 'function') res.catch(() => {});
        } catch (e) {
            console.warn('[media] source control failed', e);
        }
    }
}
